import { createLogic } from 'redux-logic';
import ActionConstants from '../constants/actions';
import ApiConstants from '../constants/apiConstants';
import http from '../utils/http';

const getAllRoleGroupsLogic = createLogic({
	type: ActionConstants.RoleGroupsConstants.GET_ALL_ROLE_GROUPS,
	latest: true,
	process({ action }, dispatch, done) {
		dispatch({ type: ActionConstants.RoleGroupsConstants.GET_ALL_ROLE_GROUPS_INIT });

		const brandId = window?.LTD?.getSiteInfo?.()?.brandId;
		const offset = action?.payload?.offset ?? 0;
		const size = action?.payload?.size ?? 10;
		const searchParam = action?.payload?.searchTerm;

		if (!brandId) {
			dispatch({
				type: ActionConstants.RoleGroupsConstants.GET_ALL_ROLE_GROUPS_ERROR,
				payload: 'brandId is required',
			});
			done();
			return;
		}

		const queryParams = { offset, size };
		if (searchParam) {
			queryParams.searchTerm = searchParam;
		}

		const url = ApiConstants.ROLE_GROUPS.GET_ALL.URL(brandId);
		return http
			.get(url, {}, queryParams)
			.then((res) => {
				dispatch({
					type: ActionConstants.RoleGroupsConstants.GET_ALL_ROLE_GROUPS_SUCCESS,
					payload: res,
				});
				done();
			})
			.catch((err) => {
				const message =
					err?.response?.data?.message ||
					err?.response?.data?.error ||
					err?.message ||
					'Failed to fetch role groups';

				dispatch({
					type: ActionConstants.RoleGroupsConstants.GET_ALL_ROLE_GROUPS_ERROR,
					payload: String(message),
				});
				done();
			});
	},
	warnTimeout: 0,
});

const updateRoleGroupStatusLogic = createLogic({
	type: ActionConstants.RoleGroupsConstants.UPDATE_ROLE_GROUP_STATUS,
	latest: true,
	process({ action }, dispatch, done) {
		const { role, newStatus, offset, size, searchTerm, onSuccess, onError } = action.payload;
		
		const url = ApiConstants.ROLE_GROUPS.UPDATE_STATUS.URL(role.roleGroupId);
		
		const body = {
			...role,
			status: newStatus
		};

		return http.put(url, {}, body)
			.then(() => {
				if (onSuccess) onSuccess();
				// Re-fetch role groups to reflect changes
				dispatch({
					type: ActionConstants.RoleGroupsConstants.GET_ALL_ROLE_GROUPS,
					payload: { offset, size, searchTerm }
				});
				done();
			})
			.catch((err) => {
				const message = err?.response?.data?.message || err?.message || 'Failed to update role status';
				if (onError) onError(message);
				console.error('Error updating role status:', err);
				done();
			});
	}
});

const deleteRoleGroupLogic = createLogic({
	type: ActionConstants.RoleGroupsConstants.DELETE_ROLE_GROUP,
	latest: true,
	process({ action }, dispatch, done) {
		const { role, offset, size, searchTerm, onSuccess, onError } = action.payload;

		const url = ApiConstants.ROLE_GROUPS.DELETE.URL(role.roleGroupId);

		return http.delete(url)
			.then(() => {
				if (onSuccess) onSuccess();
				// Re-fetch role groups to reflect the deletion
				dispatch({
					type: ActionConstants.RoleGroupsConstants.GET_ALL_ROLE_GROUPS,
					payload: { offset, size, searchTerm }
				});
				done();
			})
			.catch((err) => {
				const message = err?.response?.data?.message || err?.message || 'Failed to delete role group';
				if (onError) onError(message);
				console.error('Error deleting role group:', err);
				done();
			});
	}
});

const createRoleGroupLogic = createLogic({
	type: ActionConstants.RoleGroupsConstants.CREATE_ROLE_GROUP,
	latest: true,
	process({ action }, dispatch, done) {
		const { payload, offset, size, searchTerm, onSuccess, onError } = action.payload;
		dispatch({ type: ActionConstants.RoleGroupsConstants.CREATE_ROLE_GROUP_LOADING });

		const url = ApiConstants.ROLE_GROUPS.CREATE.URL();
		return http.post(url, {}, payload)
			.then((res) => {
				dispatch({ type: ActionConstants.RoleGroupsConstants.CREATE_ROLE_GROUP_SUCCESS });
				if (onSuccess) onSuccess();
				// Re-fetch role groups
				dispatch({
					type: ActionConstants.RoleGroupsConstants.GET_ALL_ROLE_GROUPS,
					payload: { offset, size, searchTerm }
				});
				done();
			})
			.catch((err) => {
				const message = err?.response?.data?.message || err?.message || 'Failed to create role';
				dispatch({
					type: ActionConstants.RoleGroupsConstants.CREATE_ROLE_GROUP_ERROR,
					payload: String(message)
				});
				if (onError) onError(message);
				done();
			});
	}
});

const editRoleGroupLogic = createLogic({
	type: ActionConstants.RoleGroupsConstants.EDIT_ROLE_GROUP,
	latest: true,
	process({ action }, dispatch, done) {
		const { roleGroupId, payload, offset, size, searchTerm, onSuccess, onError } = action.payload;
		dispatch({ type: ActionConstants.RoleGroupsConstants.EDIT_ROLE_GROUP_LOADING });

		const url = ApiConstants.ROLE_GROUPS.UPDATE_ROLE_COMPLETE.URL(roleGroupId);
		return http.put(url, {}, payload)
			.then((res) => {
				dispatch({ type: ActionConstants.RoleGroupsConstants.EDIT_ROLE_GROUP_SUCCESS });
				if (onSuccess) onSuccess();
				// Re-fetch role groups
				dispatch({
					type: ActionConstants.RoleGroupsConstants.GET_ALL_ROLE_GROUPS,
					payload: { offset, size, searchTerm }
				});
				done();
			})
			.catch((err) => {
				const message = err?.response?.data?.message || err?.message || 'Failed to update role';
				dispatch({
					type: ActionConstants.RoleGroupsConstants.EDIT_ROLE_GROUP_ERROR,
					payload: String(message)
				});
				if (onError) onError(message);
				done();
			});
	}
});

const getRoleGroupLogic = createLogic({
	type: ActionConstants.RoleGroupsConstants.GET_ROLE_GROUP,
	latest: true,
	process({ action }, dispatch, done) {
		const { roleGroupId } = action.payload;
		dispatch({ type: ActionConstants.RoleGroupsConstants.GET_ROLE_GROUP_LOADING });

		const url = ApiConstants.ROLE_GROUPS.GET_BY_ID.URL(roleGroupId);
		return http.get(url, {})
			.then((res) => {
				dispatch({
					type: ActionConstants.RoleGroupsConstants.GET_ROLE_GROUP_SUCCESS,
					payload: res?.result || {},
				});
				done();
			})
			.catch((err) => {
				const message = err?.response?.data?.message || err?.message || 'Failed to fetch role details';
				dispatch({
					type: ActionConstants.RoleGroupsConstants.GET_ROLE_GROUP_ERROR,
					payload: String(message)
				});
				done();
			});
	}
});

const RolesActions = [
	getAllRoleGroupsLogic,
	updateRoleGroupStatusLogic,
	deleteRoleGroupLogic,
	createRoleGroupLogic,
	editRoleGroupLogic,
	getRoleGroupLogic
];

export default RolesActions;
